import { useContext } from 'react';
import {
  AGENT_FAILURE_ACTIONS_MAX,
  configWithFailureActions,
  failureActionsFromConfig,
  type AgentFailureActionDraft,
} from '@erp/shared-types';
import { ChatTextIcon, FlowArrowIcon, TrashIcon } from '@phosphor-icons/react';
import { T, useGT, Var } from 'gt-react';
import { useTwilioAccount } from 'src/api/rpc/hooks/twilio/useTwilioAccount';
import { AuthContext } from 'src/context/auth.context';
import { useListWorkflowDefinitionSummaries } from 'src/api/rpc/hooks/workflows/useWorkflowDefinitions';
import {
  failureSmsRecipientInvalid,
  failureSmsSenderChoices,
  senderSelectDisabled,
  showMissingSenderHint,
} from './failureSmsSenders';
import { Button } from 'src/components/ui/button';
import { Input } from 'src/components/ui/input';
import { Label } from 'src/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'src/components/ui/select';
import { Textarea } from 'src/components/ui/textarea';

/**
 * What to do when a run of this agent fails. Stored on `config.failureActions`
 * and executed from the published version when the run is marked failed.
 */
export function FailureActionsSection({
  config,
  onChange,
}: {
  config: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
}) {
  const gt = useGT();
  const { currentUser } = useContext(AuthContext);
  const actions = failureActionsFromConfig(config);
  const twilioAccount = useTwilioAccount({
    organizationId: currentUser?.organizationId,
  });
  const senderNumbers = twilioAccount.data?.phoneNumbers ?? [];
  const { data, isLoading, isError } = useListWorkflowDefinitionSummaries({
    published: true,
    limit: 100,
    sortBy: 'name',
    sortDirection: 'asc',
  });
  const workflows = (data?.workflows ?? []).filter(
    (workflow) => workflow.isActive && workflow.pinnedVersionId,
  );
  const atCap = actions.length >= AGENT_FAILURE_ACTIONS_MAX;

  function commit(next: AgentFailureActionDraft[]) {
    onChange(configWithFailureActions(config, next));
  }

  function updateAction(index: number, action: AgentFailureActionDraft) {
    commit(actions.map((current, i) => (i === index ? action : current)));
  }

  function removeAction(index: number) {
    commit(actions.filter((_, i) => i !== index));
  }

  function addWorkflow() {
    commit([...actions, { type: 'workflow', workflowId: '' }]);
  }

  function addSms() {
    const onlySender = senderNumbers.length === 1 ? senderNumbers[0].id : '';
    commit([
      ...actions,
      {
        type: 'sms',
        fromNumberId: onlySender,
        to: '',
        message: gt('Agent {agentName} failed: {error}', {
          context: 'default text message sent when an agent run fails',
          agentName: '{agentName}',
          error: '{error}',
        }),
      },
    ]);
  }

  return (
    <div className="max-w-3xl space-y-3 rounded-md border p-3">
      <div className="space-y-1">
        <h3 className="text-sm font-semibold">
          <T context="agent definition section for what happens when a run fails">
            When this agent fails
          </T>
        </h3>
        <p className="text-xs text-muted-foreground">
          <T context="explains that failure actions run after an agent run is marked failed">
            These run after a failed run is recorded. They use the published
            version of this agent.
          </T>
        </p>
      </div>

      {actions.length === 0 && (
        <p className="text-sm text-muted-foreground">
          <T context="empty state when an agent has no failure actions">
            Nothing happens when a run fails.
          </T>
        </p>
      )}

      <ul className="space-y-2">
        {actions.map((action, index) => (
          <li key={index} className="space-y-2 rounded-md border p-2">
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs font-medium">
                {action.type === 'workflow' ? (
                  <T context="failure action that starts a workflow">
                    Start a workflow
                  </T>
                ) : (
                  <T context="failure action that sends an SMS">Send a text</T>
                )}
              </p>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                aria-label={gt('Remove failure action', {
                  context: 'remove a configured agent failure action',
                })}
                onClick={() => removeAction(index)}
              >
                <TrashIcon className="h-4 w-4 text-destructive" />
              </Button>
            </div>
            {action.type === 'workflow' ? (
              <WorkflowFailureAction
                workflowId={action.workflowId}
                workflows={workflows}
                isLoading={isLoading}
                isError={isError}
                onChange={(workflowId) =>
                  updateAction(index, { type: 'workflow', workflowId })
                }
              />
            ) : (
              <SmsFailureAction
                index={index}
                fromNumberId={action.fromNumberId}
                to={action.to}
                message={action.message}
                senderNumbers={senderNumbers}
                numbersLoading={twilioAccount.isLoading}
                numbersError={twilioAccount.isError}
                onChange={(next) =>
                  updateAction(index, { type: 'sms', ...next })
                }
              />
            )}
          </li>
        ))}
      </ul>

      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={atCap}
          onClick={addWorkflow}
        >
          <FlowArrowIcon className="mr-1 h-4 w-4" />
          <T context="button to add a workflow failure action">
            Start a Workflow
          </T>
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={atCap}
          onClick={addSms}
        >
          <ChatTextIcon className="mr-1 h-4 w-4" />
          <T context="button to add an SMS failure action">Send a Text</T>
        </Button>
      </div>
      {atCap && (
        <p className="text-xs text-muted-foreground">
          <T context="cap on how many failure actions one agent can have">
            This agent already has the maximum of 5 failure actions.
          </T>
        </p>
      )}
    </div>
  );
}

function WorkflowFailureAction({
  workflowId,
  workflows,
  isLoading,
  isError,
  onChange,
}: {
  workflowId: string;
  workflows: Array<{ id: string; name: string }>;
  isLoading: boolean;
  isError: boolean;
  onChange: (workflowId: string) => void;
}) {
  const gt = useGT();
  const known = workflows.some((workflow) => workflow.id === workflowId);
  const choices =
    workflowId && !known
      ? [
          {
            id: workflowId,
            name: gt('Saved workflow', {
              context: 'failure-action workflow that is not in the loaded list',
            }),
          },
          ...workflows,
        ]
      : workflows;

  return (
    <div className="space-y-1">
      <Label>
        <T context="label for the workflow started when an agent fails">
          Workflow
        </T>
      </Label>
      {isError ? (
        <p className="text-xs text-destructive">
          <T context="error when the failure-action workflow picker fails to load">
            Failed to load workflows.
          </T>
        </p>
      ) : (
        <Select
          value={workflowId || undefined}
          onValueChange={(value) => {
            if (value) onChange(value);
          }}
          disabled={isLoading}
        >
          <SelectTrigger>
            <SelectValue
              placeholder={
                isLoading
                  ? gt('Loading workflows', {
                      context: 'placeholder while workflows load',
                    })
                  : gt('Choose a workflow', {
                      context:
                        'placeholder to pick a workflow to start on agent failure',
                    })
              }
            />
          </SelectTrigger>
          <SelectContent>
            {choices.map((workflow) => (
              <SelectItem key={workflow.id} value={workflow.id}>
                {workflow.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      {!workflowId && (
        <p className="text-xs text-muted-foreground">
          <T context="hint that a failure workflow is not chosen yet">
            Choose a published workflow. It starts when a run fails.
          </T>
        </p>
      )}
    </div>
  );
}

function SmsFailureAction({
  index,
  fromNumberId,
  to,
  message,
  senderNumbers,
  numbersLoading,
  numbersError,
  onChange,
}: {
  index: number;
  fromNumberId: string;
  to: string;
  message: string;
  senderNumbers: Array<{ id: string; phoneNumber: string }>;
  numbersLoading: boolean;
  numbersError: boolean;
  onChange: (next: {
    fromNumberId: string;
    to: string;
    message: string;
  }) => void;
}) {
  return (
    <div className="space-y-2">
      <SmsSenderField
        fromNumberId={fromNumberId}
        to={to}
        message={message}
        senderNumbers={senderNumbers}
        numbersLoading={numbersLoading}
        numbersError={numbersError}
        onChange={onChange}
      />
      <SmsRecipientField
        index={index}
        fromNumberId={fromNumberId}
        to={to}
        message={message}
        onChange={onChange}
      />
      <SmsMessageField
        fromNumberId={fromNumberId}
        to={to}
        message={message}
        onChange={onChange}
      />
    </div>
  );
}

function SmsSenderField({
  fromNumberId,
  to,
  message,
  senderNumbers,
  numbersLoading,
  numbersError,
  onChange,
}: {
  fromNumberId: string;
  to: string;
  message: string;
  senderNumbers: Array<{ id: string; phoneNumber: string }>;
  numbersLoading: boolean;
  numbersError: boolean;
  onChange: (next: {
    fromNumberId: string;
    to: string;
    message: string;
  }) => void;
}) {
  const gt = useGT();
  const senderChoices = failureSmsSenderChoices(
    senderNumbers,
    fromNumberId,
    gt('Saved number', {
      context: 'failure-action sender that is no longer in Twilio settings',
    }),
  );

  return (
    <div className="space-y-1">
      <Label>
        <T context="Twilio number a failure text is sent from">From</T>
      </Label>
      <SmsSenderControl
        fromNumberId={fromNumberId}
        to={to}
        message={message}
        senderChoices={senderChoices}
        numbersLoading={numbersLoading}
        numbersError={numbersError}
        onChange={onChange}
      />
      <MissingSenderHint
        visible={showMissingSenderHint({
          senderCount: senderNumbers.length,
          loading: numbersLoading,
          error: numbersError,
        })}
      />
    </div>
  );
}

function SmsSenderControl({
  fromNumberId,
  to,
  message,
  senderChoices,
  numbersLoading,
  numbersError,
  onChange,
}: {
  fromNumberId: string;
  to: string;
  message: string;
  senderChoices: Array<{ id: string; phoneNumber: string }>;
  numbersLoading: boolean;
  numbersError: boolean;
  onChange: (next: {
    fromNumberId: string;
    to: string;
    message: string;
  }) => void;
}) {
  const gt = useGT();
  if (numbersError) {
    return (
      <p className="text-xs text-destructive">
        <T context="error when Twilio sender numbers fail to load">
          Failed to load Twilio numbers.
        </T>
      </p>
    );
  }

  return (
    <Select
      value={fromNumberId || undefined}
      onValueChange={(value) => {
        if (value) onChange({ fromNumberId: value, to, message });
      }}
      disabled={senderSelectDisabled(numbersLoading, senderChoices.length)}
    >
      <SelectTrigger>
        <SelectValue placeholder={smsSenderPlaceholder(gt, numbersLoading)} />
      </SelectTrigger>
      <SelectContent>
        {senderChoices.map((number) => (
          <SelectItem key={number.id} value={number.id}>
            {number.phoneNumber}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function smsSenderPlaceholder(
  gt: ReturnType<typeof useGT>,
  numbersLoading: boolean,
): string {
  if (numbersLoading) {
    return gt('Loading numbers', {
      context: 'placeholder while Twilio sender numbers load',
    });
  }
  return gt('Choose a sender', {
    context:
      'placeholder to pick the Twilio number a failure text is sent from',
  });
}

function MissingSenderHint({ visible }: { visible: boolean }) {
  if (!visible) return null;
  return (
    <p className="text-xs text-muted-foreground">
      <T context="hint when no Twilio sender numbers exist">
        Add a sender number in{' '}
        <a className="underline" href="/settings/organization/twilio">
          Twilio settings
        </a>{' '}
        before this text can send.
      </T>
    </p>
  );
}

function SmsRecipientField({
  index,
  fromNumberId,
  to,
  message,
  onChange,
}: {
  index: number;
  fromNumberId: string;
  to: string;
  message: string;
  onChange: (next: {
    fromNumberId: string;
    to: string;
    message: string;
  }) => void;
}) {
  return (
    <div className="space-y-1">
      <Label htmlFor={`failure-sms-to-${index}`}>
        <T context="phone number that receives an agent-failure text">
          Recipient
        </T>
      </Label>
      <Input
        id={`failure-sms-to-${index}`}
        type="tel"
        inputMode="tel"
        autoComplete="tel"
        placeholder="+15551234567"
        value={to}
        onChange={(event) =>
          onChange({ fromNumberId, to: event.target.value, message })
        }
      />
      <RecipientInvalidHint invalid={failureSmsRecipientInvalid(to)} />
    </div>
  );
}

function RecipientInvalidHint({ invalid }: { invalid: boolean }) {
  if (!invalid) return null;
  return (
    <p className="text-xs text-destructive">
      <T context="validation hint for an agent failure SMS number">
        Use E.164 format, like +15551234567. This text will not send until the
        number is valid.
      </T>
    </p>
  );
}

function SmsMessageField({
  fromNumberId,
  to,
  message,
  onChange,
}: {
  fromNumberId: string;
  to: string;
  message: string;
  onChange: (next: {
    fromNumberId: string;
    to: string;
    message: string;
  }) => void;
}) {
  const gt = useGT();
  return (
    <div className="space-y-1">
      <Label>
        <T context="body of the text sent when an agent fails">Message</T>
      </Label>
      <Textarea
        rows={3}
        value={message}
        placeholder={gt('Agent {agentName} failed: {error}', {
          context: 'placeholder for an agent failure SMS template',
          agentName: '{agentName}',
          error: '{error}',
        })}
        onChange={(event) =>
          onChange({ fromNumberId, to, message: event.target.value })
        }
      />
      <p className="text-xs text-muted-foreground">
        <T context="placeholders allowed in an agent failure SMS">
          The message can include <Var>{'{agentName}'}</Var>,{' '}
          <Var>{'{error}'}</Var>, and <Var>{'{runId}'}</Var>.
        </T>
      </p>
    </div>
  );
}
